import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useHttp from '../../../hooks/http.hook';
import '../../business/Business.css';
import '../OrdersPage.css';
import CreateBusinessHeader from '../../business/components/CreateBusinessHeader';
import DateRangeCalendarModal, {
  formatDateFilterLabel,
} from '../components/DateRangeCalendarModal';
import OrderFilterSelectModal from '../components/OrderFilterSelectModal';
import {
  PaymentRecordStatusTypes,
  PAYMENT_RECORD_STATUS_OPTIONS,
} from '../../../types/PaymentRecordStatusTypes';
import { PaymentProviderTypes } from '../../../types/PaymentProviderTypes';
import { formatPaymentShortDate } from '../components/dateUtils';

const PAGE_SIZE = 20;
const PAYMENT_LABELS = PaymentRecordStatusTypes.labels;

function parseStatusIdsParam(value) {
  if (!value) return [];
  return String(value)
    .split(',')
    .map((id) => Number.parseInt(id.trim(), 10))
    .filter((id) => Number.isFinite(id));
}

function parseDateParam(value) {
  const raw = String(value ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
}

function applyPaymentsFilterParams(prev, updates) {
  const next = new URLSearchParams(prev);
  Object.entries(updates).forEach(([key, value]) => {
    if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
      next.delete(key);
      return;
    }
    next.set(key, Array.isArray(value) ? value.join(',') : String(value));
  });
  if (!next.get('dateFrom')) {
    next.delete('dateTo');
  }
  return next;
}

function formatStatusFilterLabel(statusIds) {
  if (!statusIds.length) return 'Статусы';
  const labels = PAYMENT_RECORD_STATUS_OPTIONS
    .filter((item) => statusIds.includes(item.id))
    .map((item) => item.label);
  return `Статусы (${labels.join(', ')})`;
}

function formatMoney(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return '0.00 сом';
  return `${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} сом`;
}

function paymentOrder(payment) {
  return payment?.order ?? payment?.Order ?? {};
}

function paymentProviderLabel(payment) {
  const provider = payment?.provider ?? {};
  const providerType = Number(provider?.providerType ?? provider?.provider_type);
  if (providerType === PaymentProviderTypes.O_TRADE) return 'OБанк!';
  return 'Наличные';
}

function getPaymentDateKey(payment) {
  const raw = payment?.createdAt ?? payment?.created_at;
  if (!raw) return '';
  const d = typeof raw === 'string' ? new Date(raw) : raw;
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateLabel(dateKey) {
  if (!dateKey) return dateKey;
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date();
  const isToday = date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();
  if (isToday) return 'Сегодня';
  if (isYesterday) return 'Вчера';
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function groupPaymentsByDate(payments) {
  const byDate = {};
  payments.forEach((payment) => {
    const key = getPaymentDateKey(payment);
    if (!key) return;
    if (!byDate[key]) byDate[key] = [];
    byDate[key].push(payment);
  });
  const keys = Object.keys(byDate).sort((a, b) => b.localeCompare(a));
  return keys.map((key) => ({
    dateKey: key,
    dateLabel: formatDateLabel(key),
    payments: byDate[key],
  }));
}

function PaymentsPage() {
  const navigate = useNavigate();
  const { branchId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { requestWithMeta } = useHttp();

  const [items, setItems] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  const [statusModalOpen, setStatusModalOpen] = React.useState(false);

  const selectedStatusKey = searchParams.get('statusIds') || '';
  const selectedStatusIds = React.useMemo(
    () => parseStatusIdsParam(selectedStatusKey),
    [selectedStatusKey]
  );
  const dateFrom = parseDateParam(searchParams.get('dateFrom'));
  const dateTo = parseDateParam(searchParams.get('dateTo')) || dateFrom;

  const contentRef = React.useRef(null);
  const sentinelRef = React.useRef(null);

  const hasMore = page < totalPages;
  const dateGroups = React.useMemo(() => groupPaymentsByDate(items), [items]);
  const allStatusIds = PAYMENT_RECORD_STATUS_OPTIONS.map(({ id }) => id);
  const dateFilterKey = `${dateFrom || ''}|${dateTo || ''}`;
  const dateFilterSelected = Boolean(dateFrom);
  const statusFilterSelected = selectedStatusIds.length > 0;
  const paymentsSearch = searchParams.toString();

  const setFilterParams = React.useCallback((updates) => {
    setSearchParams((prev) => applyPaymentsFilterParams(prev, updates), { replace: true });
  }, [setSearchParams]);

  const openOrder = (orderId) => {
    if (!orderId) return;
    navigate(
      {
        pathname: `/branch/${branchId}/orders/${orderId}`,
        search: paymentsSearch,
      },
      {
        state: {
          backTo: {
            pathname: `/branch/${branchId}/payments`,
            search: paymentsSearch,
          },
        },
      }
    );
  };

  const fetchPage = React.useCallback(
    async (pageNum) => {
      const params = new URLSearchParams({
        page: String(pageNum),
        limit: String(PAGE_SIZE),
      });
      if (selectedStatusIds.length > 0) {
        params.set('statusIds', selectedStatusIds.join(','));
      }
      if (dateFrom) {
        params.set('dateFrom', dateFrom);
        params.set('dateTo', dateTo || dateFrom);
      }
      const res = await requestWithMeta(
        `/operator/branches/${encodeURIComponent(branchId)}/payments?${params.toString()}`,
        'GET'
      );
      if (!res?.ok) {
        return { ok: false, rows: [], totalPages: 1 };
      }
      const list = Array.isArray(res?.data?.payments) ? res.data.payments : [];
      const pagination = res?.data?.pagination ?? {};
      return {
        ok: true,
        rows: list,
        totalPages: pagination.totalPages ?? 1,
      };
    },
    [branchId, requestWithMeta, selectedStatusIds, dateFrom, dateTo]
  );

  React.useEffect(() => {
    if (!branchId) {
      navigate('/branch/store', { replace: true });
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(1);
    setItems([]);
    setTotalPages(1);

    (async () => {
      try {
        const result = await fetchPage(1);
        if (cancelled) return;
        if (!result.ok) {
          setError('Не удалось загрузить оплаты');
          setItems([]);
          return;
        }
        setItems(result.rows);
        setTotalPages(result.totalPages);
      } catch (_) {
        if (!cancelled) {
          setError('Ошибка загрузки');
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [branchId, navigate, fetchPage, selectedStatusKey, dateFilterKey]);

  React.useEffect(() => {
    const root = contentRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [e] = entries;
        if (!e?.isIntersecting) return;
        if (loading || loadingMore || page >= totalPages) return;

        setLoadingMore(true);
        fetchPage(page + 1)
          .then((result) => {
            if (!result.ok) return;
            setItems((prev) => [...prev, ...result.rows]);
            setPage((p) => p + 1);
            setTotalPages(result.totalPages);
          })
          .finally(() => setLoadingMore(false));
      },
      { root, rootMargin: '100px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchPage, hasMore, loading, loadingMore, page, totalPages]);

  const handleBack = () => {
    navigate(`/branch/${branchId}`);
  };

  const handleApplyDateFilter = ({ start, end }) => {
    setFilterParams({
      dateFrom: start || null,
      dateTo: end || start || null,
    });
  };

  const handleApplyStatusFilter = (ids) => {
    const unique = [...new Set(ids)];
    const isAll =
      unique.length === 0 ||
      (unique.length === allStatusIds.length && allStatusIds.every((id) => unique.includes(id)));
    setFilterParams({ statusIds: isAll ? [] : unique });
  };

  return (
    <div className="orders-page-root">
      <CreateBusinessHeader
        title="Оплаты"
        subtitle=""
        onBack={handleBack}
      />

      <main ref={contentRef} className="orders-page-content">
        <div className="orders-page-statusFilters" role="group" aria-label="Фильтр оплат">
          <button
            type="button"
            className={`orders-page-statusFilterTag${dateFilterSelected ? ' is-selected' : ''}`}
            aria-pressed={dateFilterSelected}
            onClick={() => setCalendarOpen(true)}
          >
            {formatDateFilterLabel(dateFrom, dateTo)}
          </button>
          <button
            type="button"
            className={`orders-page-statusFilterTag${statusFilterSelected ? ' is-selected' : ''}`}
            aria-pressed={statusFilterSelected}
            onClick={() => setStatusModalOpen(true)}
          >
            {formatStatusFilterLabel(selectedStatusIds)}
          </button>
        </div>

        {loading ? (
          <div className="orders-page-loading" aria-busy="true">
            <div className="orders-page-skeleton orders-page-skeleton--block" />
            <div className="orders-page-skeleton orders-page-skeleton--block" />
            <div className="orders-page-skeleton orders-page-skeleton--block" />
          </div>
        ) : error ? (
          <div className="orders-page-error">{error}</div>
        ) : dateGroups.length === 0 ? (
          <div className="orders-page-empty">Нет оплат</div>
        ) : (
          <>
            {dateGroups.map((group) => (
              <section key={group.dateKey} className="orders-page-dateSection">
                <h2 className="orders-page-dateHeading">{group.dateLabel}</h2>
                <div className="orders-page-tableWrap">
                  <table className="orders-page-table orders-page-table--payments" aria-label={`Оплаты за ${group.dateLabel}`}>
                    <thead>
                      <tr>
                        <th scope="col">Сумма</th>
                        <th scope="col">Статус</th>
                        <th scope="col">Способ</th>
                        <th scope="col" className="orders-page-colDesktop">Дата</th>
                        <th scope="col" className="orders-page-colDesktop">Клиент</th>
                        <th scope="col" className="orders-page-colDesktop">Заказ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.payments.map((payment) => {
                        const id = payment?.id ?? payment?._id;
                        const order = paymentOrder(payment);
                        const orderId = order?.id ?? payment?.orderId ?? payment?.order_id;
                        const statusId = Number(
                          payment?.statusId ?? payment?.status_id ?? PaymentRecordStatusTypes.PAID
                        );
                        const customerName = order?.customerName ?? order?.customer_name ?? '—';

                        return (
                          <tr
                            key={id}
                            className="orders-page-row"
                            tabIndex={0}
                            onClick={() => openOrder(orderId)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openOrder(orderId);
                              }
                            }}
                          >
                            <td className="orders-page-itemId">{formatMoney(payment?.amount)}</td>
                            <td>
                              <span className={`orders-page-itemPayment orders-page-itemPayment--record${statusId}`}>
                                {PAYMENT_LABELS[statusId] ?? `Статус ${statusId}`}
                              </span>
                            </td>
                            <td className="orders-page-itemProvider">{paymentProviderLabel(payment)}</td>
                            <td className="orders-page-colDesktop">
                              {formatPaymentShortDate(payment?.createdAt ?? payment?.created_at)}
                            </td>
                            <td className="orders-page-colDesktop orders-page-itemCustomer">{customerName}</td>
                            <td className="orders-page-colDesktop orders-page-itemOrder">{orderId ? `Заказ № ${orderId}` : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
            <div ref={sentinelRef} className="orders-page-sentinel" aria-hidden="true" />
            {loadingMore ? (
              <div className="orders-page-loadingMore" aria-busy="true">
                <div className="orders-page-skeleton orders-page-skeleton--block" />
                <div className="orders-page-skeleton orders-page-skeleton--block" />
              </div>
            ) : null}
          </>
        )}
      </main>

      <footer className="orders-page-footerRow">
        <button
          type="button"
          className="orders-page-footerButton orders-page-footerButtonPrimary"
          onClick={() => navigate(`/branch/${branchId}/payment-providers`)}
        >
          Способ оплаты
        </button>
        <button
          type="button"
          className="orders-page-footerButton orders-page-footerButtonPrimary"
          onClick={() => navigate(`/branch/${branchId}/receipts`)}
        >
          Чеки
        </button>
      </footer>

      <DateRangeCalendarModal
        isOpen={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        initialStart={dateFrom}
        initialEnd={dateTo}
        onApply={handleApplyDateFilter}
      />
      <OrderFilterSelectModal
        isOpen={statusModalOpen}
        title="Статусы"
        options={PAYMENT_RECORD_STATUS_OPTIONS}
        selectedIds={selectedStatusIds}
        multiple
        resetIds={allStatusIds}
        onApply={handleApplyStatusFilter}
        onClose={() => setStatusModalOpen(false)}
      />
    </div>
  );
}

export default PaymentsPage;
